import React, { useEffect, useMemo } from "react";
import styles from "./styles.module.sass";
import { useDispatch } from "react-redux";
import { useSelect } from "../../../../hooks/useSelect";
import { LoadingPageInfo } from "../../../../Components/Leaderboard/LoadingPageInfo";
import { LoadingStatus } from "../../../../Components/Pickup/redux-store/reducers/withLoadingState";
import { Plus } from "react-feather";
import {
  getVouchers,
  modifyReferralGift,
} from "../../../../redux-store/actions";
import { IPromocode } from "../../../../types";
import CouponIcon from "../../../../assets/coupon.png";
import { useGetCurrency } from "../../../../Utils/getLocalizedCurrency";
import { extractReferralGiftData } from "../../../../helpers/referral-gifts/extractReferralGiftData";
import { formatVoucherDisplayName } from "../../../../helpers/referral-gifts/formatVoucherDisplayName";

interface ReferralPromoListProps {
  onItemSelect?: (item: IPromocode) => void;
}

function ReferralPromoList({ onItemSelect }: ReferralPromoListProps) {
  const dispatch = useDispatch();
  const { loadingStatus, selectedReferralGiftRow, pendingChanges,
    promoList: initialPromoList,
    senderPromoList,
    shownPromoList } =
    useSelect((state) => ({
      loadingStatus: state.getVouchersReducer.loadingStatus,
      selectedReferralGiftRow: state.referralGiftsReducer.selectedRow,
      pendingChanges: state.referralGiftsReducer.pendingChanges,
      promoList: state.getVouchersReducer.promoList || [],
      senderPromoList: state.getVouchersReducer.senderPromoList || [],
      shownPromoList: state.getVouchersReducer.shownPromoList || []
    }));

  const activeColumn = selectedReferralGiftRow?.columnType;

  const { promoList } = useMemo(() => {
    if (!activeColumn) {
      return {
        promoList: initialPromoList
      };
    }

    return {
      promoList: activeColumn === "sender"
        ? senderPromoList
        : shownPromoList
    };
  }, [activeColumn, initialPromoList, senderPromoList, shownPromoList]);

  const currency = useGetCurrency();

  useEffect(() => {
    dispatch(getVouchers({ is_voucher: true }));
  }, [dispatch]);

  const handleSelectVoucher = (voucher: IPromocode) => {
    if (!selectedReferralGiftRow) return;
    const existingChanges = pendingChanges[selectedReferralGiftRow.index];
    const voucherDisplayName = formatVoucherDisplayName(voucher, currency);
    const giftType: "promotion" | "item" = "promotion";

    const senderData =
      selectedReferralGiftRow.columnType === "sender"
        ? {
          sender_referral_gift_id: voucher.id,
          sender_referral_gift_type: giftType,
          sender_referral_gift_price:
            voucher.discount_value || voucher.cashback_value || 0,
          sender_referral_gift_name: voucherDisplayName,
          sender_referral_gift_image: CouponIcon,
        }
        : existingChanges?.data
          ? extractReferralGiftData(existingChanges.data, "sender")
          : {};

    const recipientData =
      selectedReferralGiftRow.columnType === "recipient"
        ? {
          recipient_referral_gift_id: voucher.id,
          recipient_referral_gift_type: giftType,
          recipient_referral_gift_price:
            voucher.discount_value || voucher.cashback_value || 0,
          recipient_referral_gift_name: voucherDisplayName,
          recipient_referral_gift_image: CouponIcon,
        }
        : existingChanges?.data
          ? extractReferralGiftData(existingChanges.data, "recipient")
          : {};

    dispatch(
      modifyReferralGift({
        data: {
          ...senderData,
          ...recipientData,
          referral_id: selectedReferralGiftRow.referral_id,
          id: selectedReferralGiftRow.id,
        },
        prev_recipient_referral_gift_id:
          selectedReferralGiftRow.recipient_referral_gift_id ?? undefined,
        columnType: selectedReferralGiftRow.columnType,
        rowIndex: selectedReferralGiftRow.index,
      })
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.giftsWrapper}>
        {loadingStatus === LoadingStatus.loading && (
          <div className={styles.noItems}>
            <LoadingPageInfo />
          </div>
        )}

        {loadingStatus === LoadingStatus.success && promoList.length > 0 && (
          <div className={styles.categorySection}>
            <div className={styles.items}>
              {promoList.map((voucher) => (
                <div key={voucher.id} className={styles.itemContainer}>
                  <div className={styles.itemDetails}>
                    <div className={styles.itemImage}>
                      <img
                        src={CouponIcon}
                        alt={formatVoucherDisplayName(voucher, currency)}
                        className={styles.voucherImage}
                      />
                    </div>
                    <div className={styles.itemInfo}>
                      <span className={styles.itemName}>
                        {formatVoucherDisplayName(voucher, currency)} ({voucher.title})
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleSelectVoucher(voucher)}
                    className={styles.addItemBtn}
                    aria-label={`Add ${formatVoucherDisplayName(
                      voucher,
                      currency
                    )}`}
                  >
                    <Plus color="#FF9000" size={26} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {loadingStatus === LoadingStatus.success && promoList.length === 0 && (
          <div className={styles.noItems}>
            <span>No Promocodes found</span>
          </div>
        )}

        {loadingStatus === LoadingStatus.failed && (
          <div className={styles.noItems}>
            <span>Failed to load promocodes</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(ReferralPromoList);
